import { FailedResponse } from "@miragon/react-utils-api";
import { useCallback, useContext, useMemo } from "react";
import { renderError } from "../../../util/ErrorUtils";
import { SnackbarProps } from "../../Form/CustomSnackbar";
import { SnackbarContext } from "./SnackbarProvider";

type AdditionalOptions = Partial<Omit<SnackbarProps, "message"> & {
    messagePrefix: string;
}>;

interface Response {
    onError: (message: string, options?: AdditionalOptions) => void;
    onApiError: (response: FailedResponse, showRaw?: boolean, options?: AdditionalOptions) => void;
    onSuccess: (message: string, options?: AdditionalOptions) => void;
    close: () => void;
}

const useSnackbar = (): Response => {
    const context = useContext(SnackbarContext);

    if (!context) {
        throw new Error("useSnackbar must be used within a SnackbarProvider");
    }

    const {
        update,
        close
    } = context;

    const onSuccess = useCallback((message: string, options?: AdditionalOptions) => {
        update("success", message, options);
    }, [update]);

    const onError = useCallback((message: string, options?: AdditionalOptions) => {
        update("error", message, options);
    }, [update]);

    const onApiError = useCallback((response: FailedResponse, showRaw = false, options?: AdditionalOptions) => {
        const prefix = options?.messagePrefix ? `${options.messagePrefix}: ` : "";
        update("error", prefix + (renderError(response) ?? response.error), options);
    }, [update]);

    return useMemo(() => ({
        onSuccess,
        onError,
        onApiError,
        close
    }), [onSuccess, onError, onApiError, close]);
};

export default useSnackbar;
